/**
 * socketDebouncer
 * ------------------------------------------------------------------
 * Race-safe bridge between Socket.IO events and TanStack Query.
 *
 * Why this exists
 * ---------------
 * Socket events frequently arrive in bursts (e.g. a folder unwrap fans out
 * `dataroom:folderUnwrapped` and N `dataroom:fileMoved` events at once).
 * Naively calling `queryClient.invalidateQueries` for every event triggers
 * a refetch storm. Even worse: while a user-initiated mutation is in flight,
 * an in-bound socket event for the same key can race the optimistic update
 * and clobber it.
 *
 * What this module provides
 * -------------------------
 *   1. A per-query-key debouncer (default 250ms). Multiple `dataroom:*` events
 *      that target the same TanStack key collapse into a single refetch.
 *   2. A suppression refcount keyed on the same query-key. Mutations register
 *      "I'm about to mutate this key — drop incoming socket-driven invalidations
 *      until I'm done." The mutation's own `onSettled` invalidate is the
 *      authoritative refresh; replaying socket events on top of it would just
 *      re-fetch identical data.
 *   3. An `attachQueryClient(qc)` hook so the singleton boots without an
 *      import-cycle on the QueryClient module.
 *
 * Suppression always wins over debounced calls — if the count for a key is
 * non-zero, incoming socket invalidations are dropped silently. Explicit
 * `queryClient.invalidateQueries()` calls (made by mutations themselves)
 * bypass this module entirely; they go through TanStack as normal.
 *
 * Retry semantics
 * ---------------
 * TanStack mutation `onMutate` only fires once even across retries; `onSettled`
 * fires exactly once after the final attempt. So `suppressSocketInvalidation`
 * is registered once on the first attempt and released exactly once on
 * completion — no leaks, no double-decrement.
 *
 * Long-mutation behavior (>250ms)
 * -------------------------------
 * Suppression stays active for the full mutation lifetime. Any debounced
 * invalidations attempted during this window are dropped. The mutation's
 * `onSettled` invalidate replays a single fresh query — net effect: the
 * caller never sees a stale UI even if 100 socket events arrived during
 * the mutation.
 */

import type { QueryClient, QueryKey } from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// Internal singleton state
// ---------------------------------------------------------------------------

/**
 * The QueryClient is wired in at app boot via `attachQueryClient`. We do NOT
 * import it directly to avoid coupling this module to a particular construction
 * path (and to keep the module side-effect-free for tree-shaking + testing).
 */
let queryClientRef: QueryClient | null = null;

/**
 * Pending debounce timers, keyed by the JSON-serialized query key.
 *
 * Why JSON.stringify? `QueryKey` is `readonly unknown[]`. The debouncer needs
 * structural equality; reference equality won't work because callers construct
 * fresh arrays at each emit (e.g. `['dataroom', projectId]`). JSON.stringify
 * is correct for the values we care about (strings, numbers, booleans, null,
 * and nested objects of those).
 */
const pendingTimers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Suppression refcount per serialized query key. A non-zero value means
 * "drop any debounced invalidation for this key until refcount returns to zero."
 *
 * Refcount (not bool) so two concurrent mutations on the same key compose
 * correctly: each registers once and releases once; suppression lifts only
 * when both have completed.
 */
const suppressionCounts = new Map<string, number>();

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Wire the QueryClient instance. Call once at app boot. If called twice,
 * the second client wins — useful for tests that recreate the client.
 *
 * Pending timers are cleared when re-binding so they don't fire against
 * the stale (detached) client.
 */
export function attachQueryClient(qc: QueryClient): void {
  if (queryClientRef !== null && queryClientRef !== qc) {
    // Clear pending timers tied to the previous client. New invalidations
    // will be scheduled against the new client cleanly.
    for (const timer of pendingTimers.values()) {
      clearTimeout(timer);
    }
    pendingTimers.clear();
  }
  queryClientRef = qc;
}

/**
 * Schedule a debounced `invalidateQueries({ queryKey })` against the wired
 * QueryClient. Repeat calls within the debounce window for the same key
 * collapse to a single fire.
 *
 * Drops the call (no-op) when:
 *   - No QueryClient is attached (boot order bug — surfaces via missing refresh).
 *   - Suppression refcount for the key is > 0.
 */
export function debounceQueryInvalidation(queryKey: QueryKey, delayMs: number = 250): void {
  if (queryClientRef === null) {
    // Nothing to do — caller will retry on the next event after boot completes.
    return;
  }

  const serializedKey = serializeKey(queryKey);

  // Active suppression always wins.
  const suppressCount = suppressionCounts.get(serializedKey) ?? 0;
  if (suppressCount > 0) {
    return;
  }

  // Replace any in-flight timer for this key — TanStack debounce semantics.
  const existing = pendingTimers.get(serializedKey);
  if (existing !== undefined) {
    clearTimeout(existing);
  }

  const timer = setTimeout(() => {
    pendingTimers.delete(serializedKey);
    // Suppression may have arrived after the timer was scheduled but before
    // it fired. Re-check at fire time.
    const liveSuppressCount = suppressionCounts.get(serializedKey) ?? 0;
    if (liveSuppressCount > 0) {
      return;
    }
    // Defensive: if attachQueryClient was reset between schedule and fire,
    // skip silently. The new client's owners can re-trigger from their hooks.
    if (queryClientRef === null) {
      return;
    }
    void queryClientRef.invalidateQueries({ queryKey });
  }, delayMs);

  pendingTimers.set(serializedKey, timer);
}

/**
 * Register a suppression for `queryKey` and return a release function.
 * While at least one suppression is active for that key, debounced
 * invalidations are dropped.
 *
 * Pattern:
 *   const release = suppressSocketInvalidation(['dataroom', projectId]);
 *   try {
 *     await mutation.mutateAsync(...);
 *   } finally {
 *     release();
 *   }
 *
 * The returned function is idempotent — calling it more than once decrements
 * the refcount only on the first call. This protects against accidental
 * double-release in `onError` + `onSettled` flows.
 */
export function suppressSocketInvalidation(queryKey: QueryKey): () => void {
  const serializedKey = serializeKey(queryKey);
  const current = suppressionCounts.get(serializedKey) ?? 0;
  suppressionCounts.set(serializedKey, current + 1);

  // Cancel any in-flight debounce — the mutation's own onSettled invalidate
  // will replace it. We don't fire any pending timer; we drop it.
  const pending = pendingTimers.get(serializedKey);
  if (pending !== undefined) {
    clearTimeout(pending);
    pendingTimers.delete(serializedKey);
  }

  let released = false;
  return function release(): void {
    if (released) return;
    released = true;
    const remaining = (suppressionCounts.get(serializedKey) ?? 1) - 1;
    if (remaining <= 0) {
      suppressionCounts.delete(serializedKey);
    } else {
      suppressionCounts.set(serializedKey, remaining);
    }
  };
}

/**
 * TEST-ONLY: clear all internal state. Production code MUST NOT call this —
 * it would forfeit any in-flight suppressions held by active mutations.
 */
export function _resetForTests(): void {
  for (const timer of pendingTimers.values()) {
    clearTimeout(timer);
  }
  pendingTimers.clear();
  suppressionCounts.clear();
  queryClientRef = null;
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

/**
 * Stable serialization of a QueryKey for Map-based lookup.
 *
 * `JSON.stringify` is sufficient for our value space: strings, numbers,
 * booleans, null, plain objects, and nested combinations. We do not stringify
 * functions, Dates, or class instances — TanStack's own key matcher
 * (`hashKey`) does the same simplification, so this is consistent.
 */
function serializeKey(queryKey: QueryKey): string {
  return JSON.stringify(queryKey);
}
